import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Button,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import { SendAdminInvite } from "../../../services/jobService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { useDispatch } from "react-redux";

const AdminInviteDialog = ({ open, onClose }) => {
  const [emails, setEmails] = useState([""]);
  const [emailErrors, setEmailErrors] = useState([""]);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmails([""]);
      setEmailErrors([""]);
    }
  }, [open]);

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  };

  const handleAddEmailField = () => {
    setEmails([...emails, ""]);
    setEmailErrors([...emailErrors, ""]);
  };

  const handleDeleteEmailField = (index) => {
    const updatedEmails = [...emails];
    const updatedErrors = [...emailErrors];
    updatedEmails.splice(index, 1);
    updatedErrors.splice(index, 1);
    if (updatedEmails.length === 0) {
      setEmails([""]);
      setEmailErrors([""]);
    } else {
      setEmails(updatedEmails);
      setEmailErrors(updatedErrors);
    }
  };

  const handleChangeEmail = (index, value) => {
    const updatedEmails = [...emails];
    const updatedErrors = [...emailErrors];
    updatedEmails[index] = value;
    updatedErrors[index] = value.trim() && !isValidEmail(value) ? "Invalid email address" : "";
    setEmails(updatedEmails);
    setEmailErrors(updatedErrors);
  };

  const handleSendInvites = async () => {
    const trimmedEmails = emails.map(e => e.trim());
    const currentErrors = trimmedEmails.map(email =>
      email === "" ? "" : isValidEmail(email) ? "" : "Invalid email address"
    );
    setEmailErrors(currentErrors);

    const hasErrors = currentErrors.some((err) => err);
    const validEmails = trimmedEmails.filter((email, idx) => !currentErrors[idx] && email);

    if (hasErrors || validEmails.length === 0) return;

    const payload = { emails: validEmails };

    setLoading(true);
    try {
      const response = await SendAdminInvite(payload);
      if (response && response.results) {
        const sent = response.results.filter(result => result.status === "sent");
        const skipped = response.results.filter(result => result.status === "skipped");

        let message = "";
        if (sent.length > 0) {
          message += `Successfully sent ${sent.length} invite${sent.length > 1 ? 's' : ''}.`;
        }
        if (skipped.length > 0) {
          if (message) message += " ";
          message += `${skipped.length} email${skipped.length > 1 ? 's were' : ' was'} skipped (users already exist).`;
        }

        dispatch(showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: message,
        }));
        onClose();
      } else {
        dispatch(showAppAlert({
          message: "Something went wrong while sending invites. Please try again.",
          severity: "error",
        }));
      }
    } catch (error) {
      dispatch(showAppAlert({
        show: true,
        alertType: "error",
        alertMessage: error.message || "Failed to send invites. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  const isSendDisabled = emails.every((email) => email.trim() === "") || emailErrors.some((e) => e) || loading;

  return (
    <Dialog open={open} onClose={loading ? null : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Invites</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Enter email addresses to send invites. You can add or remove multiple recipients.
        </Typography>
        {emails.map((email, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              value={email}
              placeholder="Email address"
              onChange={(e) => handleChangeEmail(index, e.target.value)}
              size="small"
              disabled={loading}
              error={Boolean(emailErrors[index])}
              helperText={emailErrors[index]}
            />
            <Tooltip title="Delete">
              <span>
                <IconButton
                  onClick={() => handleDeleteEmailField(index)}
                  disabled={emails.length === 1 || loading}
                  size="small"
                >
                  <DeleteOutline />
                </IconButton>
              </span>
            </Tooltip>
            {index === emails.length - 1 && (
              <Tooltip title="Add another email">
                <IconButton
                  onClick={handleAddEmailField}
                  size="small"
                  disabled={loading}
                >
                  <AddCircleOutline />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSendInvites}
          variant="contained"
          disabled={isSendDisabled}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
              Sending...
            </>
          ) : (
            "Send Invites"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminInviteDialog;
